import React, { useEffect, useState } from 'react';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';

const PLACE_ID_RE = /^[a-zA-Z0-9_-]{20,}$/;
const TYPES_CUISINE = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'italien', label: 'Italien' },
  { value: 'japonais', label: 'Japonais' },
  { value: 'francais', label: 'Français' },
  { value: 'brasserie', label: 'Brasserie' },
  { value: 'asiatique', label: 'Asiatique' },
  { value: 'libanais', label: 'Libanais' },
  { value: 'vegetarien', label: 'Végétarien' },
  { value: 'autre', label: 'Autre' },
];

export default function AddRestaurantSheet() {
  const { isAddOpen, closeAdd, addRestaurant } = useCurrentRestaurant();

  const [placeId, setPlaceId] = useState('');
  const [nom, setNom] = useState('');
  const [ville, setVille] = useState('');
  const [typeCuisine, setTypeCuisine] = useState('restaurant');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState(null);

  // Anim slide-up
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (isAddOpen) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    return undefined;
  }, [isAddOpen]);

  // Reset form quand on ouvre/ferme
  useEffect(() => {
    if (isAddOpen) {
      setPlaceId(''); setNom(''); setVille(''); setTypeCuisine('restaurant');
      setErrors({}); setTopError(null); setSubmitting(false);
    }
  }, [isAddOpen]);

  // Lock body scroll + ESC close
  useEffect(() => {
    if (!isAddOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) closeAdd(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isAddOpen, closeAdd, submitting]);

  if (!isAddOpen) return null;

  const validate = () => {
    const e = {};
    const trimmedNom = nom.trim();
    if (trimmedNom.length < 2) e.nom = 'Nom requis (≥ 2 caractères)';
    if (ville.trim().length === 0) e.ville = 'Ville requise';
    if (placeId.trim().length > 0 && !PLACE_ID_RE.test(placeId.trim())) {
      e.placeId = 'Format invalide (Place ID Google, ex : ChIJ...)';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setTopError(null);
    setSubmitting(true);
    try {
      const result = await addRestaurant({
        nom: nom.trim(),
        place_id: placeId.trim() || null,
        ville: ville.trim(),
        type_cuisine: typeCuisine,
      });
      if (result.success) {
        // addRestaurant a déjà fait setCurrentRestaurant + refetch.
        closeAdd();
        return;
      }
      if (result.error === 'quota_exceeded') {
        setTopError('Limite atteinte. Passez à Intensif pour ajouter ce restaurant.');
      } else if (result.error === 'invalid_nom') {
        setErrors((p) => ({ ...p, nom: result.message || 'Nom invalide' }));
      } else {
        setTopError(result.message || 'Erreur d\'ajout du restaurant');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { if (!submitting) closeAdd(); };

  // Submit disabled si form invalide ou submitting
  const formValid =
    nom.trim().length >= 2
    && ville.trim().length > 0
    && (placeId.trim().length === 0 || PLACE_ID_RE.test(placeId.trim()));
  const submitDisabled = !formValid || submitting;

  return (
    <div style={styles.overlay} onClick={close} role="dialog" aria-modal="true">
      <div
        style={{ ...styles.sheet, transform: entered ? 'translateY(0)' : 'translateY(100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.handle} aria-hidden />

        <h2 style={styles.title}>Ajouter un restaurant</h2>

        {topError && <div style={styles.errorBanner}>{topError}</div>}

        <div style={styles.field}>
          <label style={styles.label}>Place ID Google <span style={styles.optional}>(optionnel)</span></label>
          <input
            type="text"
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            placeholder="ChIJ..."
            disabled={submitting}
            style={{ ...styles.input, ...(errors.placeId ? styles.inputError : null) }}
          />
          {errors.placeId
            ? <span style={styles.errorMsg}>{errors.placeId}</span>
            : <span style={styles.hint}>Trouvable sur Google Maps → Partager → Copier le lien</span>}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Nom du restaurant</label>
          <input
            type="text"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            placeholder="Maison Monicus"
            disabled={submitting}
            style={{ ...styles.input, ...(errors.nom ? styles.inputError : null) }}
          />
          {errors.nom && <span style={styles.errorMsg}>{errors.nom}</span>}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Ville</label>
          <input
            type="text"
            value={ville}
            onChange={(e) => setVille(e.target.value)}
            placeholder="Saint-Cyr-sur-Mer"
            disabled={submitting}
            style={{ ...styles.input, ...(errors.ville ? styles.inputError : null) }}
          />
          {errors.ville && <span style={styles.errorMsg}>{errors.ville}</span>}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Type de cuisine</label>
          <select
            value={typeCuisine}
            onChange={(e) => setTypeCuisine(e.target.value)}
            disabled={submitting}
            style={styles.input}
          >
            {TYPES_CUISINE.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitDisabled}
          style={{ ...styles.cta, opacity: submitDisabled ? 0.6 : 1, cursor: submitDisabled ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? 'Ajout en cours…' : 'Ajouter ce restaurant'}
        </button>

        <button type="button" onClick={close} disabled={submitting} style={styles.cancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    zIndex: 2010,
    display: 'flex',
    alignItems: 'flex-end',
  },
  sheet: {
    width: '100%',
    background: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: '12px 20px calc(env(safe-area-inset-bottom) + 16px) 20px',
    transition: 'transform 250ms cubic-bezier(0.32, 0.72, 0, 1)',
    transform: 'translateY(100%)',
    maxHeight: '90dvh',
    overflowY: 'auto',
    boxShadow: '0 -8px 30px rgba(0, 0, 0, 0.18)',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    background: '#EDEAE3',
    margin: '0 auto 16px',
  },
  title: {
    margin: '0 0 16px',
    fontSize: 18,
    fontWeight: 700,
    color: '#1A1A1A',
    letterSpacing: '-0.01em',
  },
  errorBanner: {
    background: '#FDEAE5',
    color: '#C8442A',
    padding: '10px 12px',
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 12,
    lineHeight: 1.4,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 14,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#1A1A1A',
  },
  optional: {
    fontWeight: 400,
    color: '#9A9A9A',
    fontSize: 12,
  },
  input: {
    height: 52,
    background: '#FFFFFF',
    border: '1px solid #EDEAE3',
    borderRadius: 12,
    padding: '0 16px',
    fontSize: 15,
    fontFamily: 'inherit',
    color: '#1A1A1A',
    outline: 'none',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: '#C8442A',
  },
  errorMsg: {
    color: '#C8442A',
    fontSize: 12,
  },
  hint: {
    fontSize: 12,
    color: '#888',
  },
  cta: {
    width: '100%',
    height: 52,
    background: '#E8502A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    marginTop: 8,
  },
  cancel: {
    width: '100%',
    height: 44,
    background: 'transparent',
    color: '#1A1A1A',
    border: 'none',
    fontSize: 14,
    fontWeight: 400,
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: 16,
  },
};
